import { ChangeEvent, FC, useRef, useState } from 'react'
import * as XLSX from 'xlsx'
import Modal from 'views/components/UI/Modal'
import getNineBoxUsers from 'services/competence/getNineBoxUsers'
import setUsersPerformance from 'services/competence/setUsersPerformance'
import { User } from 'views/screens/Platform-administrator/Analytics/constants/types'
import { useNotification } from 'lib/context/notification.context'
import { ButtonContainer, ButtonDisabled, StandardButton, UsersModalTitle } from './styled'

type ImportStatus = 'Validado' | 'Duplicado' | 'No encontrado' | 'Inválido'

interface ImportRow {
  rowNumber: number
  document: string
  score: string
  name: string
  status: ImportStatus
  userId?: number
  parsedScore?: number
}

interface IProps {
  show: boolean
  close: (value: boolean) => void
  processId: string
  onImportConfirmed: (userIds: number[]) => void
}

const normalizeDocument = (value: unknown): string => String(value ?? '').trim().replace(/\s+/g, '')

const parseScore = (value: unknown): number | undefined => {
  const score = String(value ?? '').trim()
  if (!/^\d+(?:[.,]\d{1,2})?$/.test(score)) return undefined

  const parsedScore = Number(score.replace(',', '.'))
  return parsedScore >= 1 && parsedScore <= 10 ? parsedScore : undefined
}

const isHeaderRow = (row: unknown[]): boolean => {
  const document = String(row[0] ?? '').trim()
  const score = String(row[1] ?? '').trim()
  return Boolean(document && score && /[a-záéíóúñ]/i.test(document) && parseScore(score) === undefined)
}

const withTimeout = async <T,>(promise: Promise<T>, timeoutMessage: string): Promise<T> => {
  let timeoutId: ReturnType<typeof setTimeout> | undefined
  const timeout = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error(timeoutMessage)), 30000)
  })

  try {
    return await Promise.race([promise, timeout])
  } finally {
    if (timeoutId) clearTimeout(timeoutId)
  }
}

const ImportUsersExcelModal: FC<IProps> = ({ show, close, processId, onImportConfirmed }) => {
  const { getError, getSuccess, getWarning } = useNotification()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [fileName, setFileName] = useState('')
  const [rows, setRows] = useState<ImportRow[]>([])
  const [isProcessing, setIsProcessing] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  const reset = () => {
    setFileName('')
    setRows([])
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleClose = () => {
    reset()
    close(false)
  }

  const getProcessUsers = async (): Promise<User[]> => {
    const firstPage = await withTimeout(
      getNineBoxUsers({ process_id: Number(processId) }, 1000, 1),
      'La consulta de usuarios del proceso tardó demasiado tiempo'
    )

    if (!firstPage || firstPage.ok === false) throw new Error(firstPage?.message || 'No fue posible consultar los usuarios')

    const users = firstPage.users ?? []
    const totalPages = Number(firstPage.total_pages ?? 1)
    for (let page = 2; page <= totalPages; page += 1) {
      const response = await withTimeout(
        getNineBoxUsers({ process_id: Number(processId) }, 1000, page),
        'La consulta de usuarios del proceso tardó demasiado tiempo'
      )
      if (!response || response.ok === false) throw new Error(response?.message || 'No fue posible consultar los usuarios')
      users.push(...(response.users ?? []))
    }

    return users
  }

  const processFile = async (file: File) => {
    const extension = file.name.split('.').pop()?.toLowerCase()
    if (!['xlsx', 'xls', 'xlsm'].includes(extension ?? '')) {
      getError('Selecciona un archivo de Excel (.xlsx, .xls o .xlsm)')
      return
    }

    setIsProcessing(true)
    try {
      const buffer = await file.arrayBuffer()
      const workbook = XLSX.read(buffer, { type: 'array' })
      const sheet = workbook.Sheets[workbook.SheetNames[0]]
      const sheetRows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '', raw: false }) as unknown[][]
      const contentRows = sheetRows.filter((row) => row.some((value) => String(value ?? '').trim() !== ''))

      if (!contentRows.length) {
        getWarning('El archivo Excel no contiene datos')
        return
      }

      if (contentRows.some((row) => row.length !== 2)) {
        getError('El Excel debe contener exactamente dos columnas: cédula y desempeño')
        return
      }

      const dataRows = isHeaderRow(contentRows[0]) ? contentRows.slice(1) : contentRows
      if (!dataRows.length) {
        getWarning('El archivo Excel no contiene filas para importar')
        return
      }

      const importedRows: ImportRow[] = []
      const seenDocuments = new Set<string>()

      for (let index = 0; index < dataRows.length; index += 1) {
        const [documentValue, scoreValue] = dataRows[index]
        const document = normalizeDocument(documentValue)
        const parsedScore = parseScore(scoreValue)
        const rowNumber = index + (dataRows.length === contentRows.length ? 1 : 2)

        if (/^\d+$/.test(document) && seenDocuments.has(document)) {
          importedRows.push({ rowNumber, document, score: String(scoreValue ?? ''), name: '', status: 'Duplicado' })
          continue
        }

        if (/^\d+$/.test(document)) seenDocuments.add(document)

        if (!/^\d+$/.test(document) || parsedScore === undefined) {
          importedRows.push({ rowNumber, document, score: String(scoreValue ?? ''), name: '', status: 'Inválido' })
          continue
        }
        importedRows.push({
          rowNumber,
          document,
          score: String(scoreValue ?? ''),
          name: '',
          status: 'No encontrado',
          parsedScore,
        })
      }

      const validRows = importedRows.filter((row) => row.status === 'No encontrado')
      const processUsers = await getProcessUsers()
      const usersByDocument = new Map(processUsers.map((user) => [normalizeDocument(user.document), user]))
      validRows.forEach((row) => {
        const user = usersByDocument.get(row.document)
        if (user) {
          row.status = 'Validado'
          row.name = user.full_name
          row.userId = user.user_id
        }
      })

      setFileName(file.name)
      setRows(importedRows)
    } catch (error) {
      console.error('Error procesando el Excel de desempeños:', error)
      getError(error instanceof Error ? error.message : 'No fue posible leer o validar el archivo Excel')
    } finally {
      setIsProcessing(false)
    }
  }

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) processFile(file)
  }

  const validRows = rows.filter((row) => row.status === 'Validado')
  const totals = rows.reduce<Record<ImportStatus, number>>(
    (total, row) => ({ ...total, [row.status]: total[row.status] + 1 }),
    { Validado: 0, Duplicado: 0, 'No encontrado': 0, Inválido: 0 }
  )

  const handleConfirm = async () => {
    if (!validRows.length) return

    setIsSaving(true)
    try {
      const response = await setUsersPerformance({
        process_id: Number(processId),
        users: validRows.map((row) => ({ user_id: row.userId as number, score: row.parsedScore as number })),
      })

      if (response?.ok === false) throw new Error(response.message)

      getSuccess(`Se actualizaron ${validRows.length} desempeño${validRows.length === 1 ? '' : 's'} correctamente`)
      onImportConfirmed(validRows.map((row) => row.userId as number))
      handleClose()
    } catch (error) {
      console.error('Error guardando los desempeños importados:', error)
      getError('No fue posible guardar los desempeños importados')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Modal styleContent={{ minWidth: '80%', minHeight: '70%', maxHeight: '85vh', overflowY: 'auto' }} show={show} setShow={handleClose}>
      <UsersModalTitle>Importar Excel Usuarios</UsersModalTitle>
      <div style={{ padding: '0 16px 24px' }}>
        <p>Selecciona un archivo Excel con dos columnas: cédula y desempeño (de 1.00 a 10.00).</p>
        <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.xlsm" onChange={handleFileChange} disabled={isProcessing || isSaving} />
        {fileName ? <p style={{ marginTop: 16 }}>Archivo: {fileName}</p> : null}

        {isProcessing ? <p>Validando usuarios del proceso...</p> : null}

        {rows.length ? (
          <>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, margin: '20px 0', fontWeight: 600 }}>
              <span>Validado: {totals.Validado}</span>
              <span>No encontrado: {totals['No encontrado']}</span>
              <span>Inválido: {totals.Inválido}</span>
              <span>Duplicado: {totals.Duplicado}</span>
            </div>
            <div style={{ overflowX: 'auto', maxHeight: 420, overflowY: 'auto', border: '1px solid #d5e1ea', borderRadius: 8 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 650 }}>
                <thead>
                  <tr style={{ background: '#f2f7fb', textAlign: 'left' }}>
                    <th style={{ padding: 12 }}>Fila</th>
                    <th style={{ padding: 12 }}>Cédula</th>
                    <th style={{ padding: 12 }}>Desempeño</th>
                    <th style={{ padding: 12 }}>Nombre</th>
                    <th style={{ padding: 12 }}>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={`${row.rowNumber}-${row.document}`} style={{ borderTop: '1px solid #e1e8ee' }}>
                      <td style={{ padding: 12 }}>{row.rowNumber}</td>
                      <td style={{ padding: 12 }}>{row.document || '-'}</td>
                      <td style={{ padding: 12 }}>{row.score || '-'}</td>
                      <td style={{ padding: 12 }}>{row.name || '-'}</td>
                      <td style={{ padding: 12 }}>{row.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : null}
      </div>
      <ButtonContainer style={{ gap: 16 }}>
        <StandardButton onClick={handleClose}>Cancelar</StandardButton>
        {validRows.length && !isProcessing && !isSaving ? (
          <StandardButton onClick={handleConfirm}>Confirmar importación</StandardButton>
        ) : (
          <ButtonDisabled>Confirmar importación</ButtonDisabled>
        )}
      </ButtonContainer>
    </Modal>
  )
}

export default ImportUsersExcelModal
